import { useEffect, useRef, useState } from 'react'
import { CHANGE_REASON_MAX_LENGTH, setChangeReasonPrompt } from '../api/client'
import { Button, Dialog, FormGrid, TextField } from './ui'

interface Pending {
  message: string | undefined
  resolve: (reason: string | null) => void
}

/**
 * F-109 (REQ-184-001, REQ-033-002): finestra "Motivo della modifica". Montata una sola volta in App, registra
 * `setChangeReasonPrompt`: quando il server risponde 428 `CHANGE_REASON_REQUIRED`, `api()` apre questa finestra e,
 * se l'utente conferma, ripete la richiesta con l'header `X-Change-Reason`. Annulla = la richiesta fallisce con un
 * `ApiError` e la modifica non viene fatta.
 */
export function ChangeReasonDialog() {
  const [pending, setPending] = useState<Pending | null>(null)
  const [reason, setReason] = useState('')
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)

  useEffect(() => {
    setChangeReasonPrompt((message?: string) => new Promise<string | null>(resolve => {
      setReason('')
      setPending({ message, resolve })
    }))
    return () => setChangeReasonPrompt(null)
  }, [])

  useEffect(() => {
    if (pending) inputRef.current?.focus()
  }, [pending])

  if (!pending) return null

  function close(value: string | null): void {
    pending?.resolve(value)
    setPending(null)
    setReason('')
  }

  const trimmed = reason.trim()

  return (
    <Dialog
      open
      onClose={() => close(null)}
      title="Motivo della modifica"
      supportingText={`${pending.message ?? 'Questa modifica richiede una motivazione.'} Il motivo resta nel registro di audit.`}
      as="form"
      onSubmit={e => {
        e.preventDefault()
        if (trimmed) close(trimmed)
      }}
      actions={
        <>
          <Button variant="text" onClick={() => close(null)}>
            Annulla
          </Button>
          <Button type="submit" variant="text" disabled={!trimmed}>
            Conferma
          </Button>
        </>
      }
    >
      <FormGrid columns={1}>
        <TextField
          ref={inputRef}
          multiline
          rows={3}
          label="Motivo della modifica"
          maxLength={CHANGE_REASON_MAX_LENGTH}
          value={reason}
          onChange={e => setReason(e.target.value)}
          supportingText={`${reason.length}/${CHANGE_REASON_MAX_LENGTH}`}
        />
      </FormGrid>
    </Dialog>
  )
}
